<template>
  <div
    ref="wrapper"
    class="relative overflow-hidden transition duration-300 ease-out"
    :class="{
      'opacity-0': transparent,
      'carousel-fade-both': showFade && hasPreviousPage && hasNextPage,
      'carousel-fade-previous': showFade && hasPreviousPage && !hasNextPage,
      'carousel-fade-next': showFade && !hasPreviousPage && hasNextPage,
      'cursor-grabbing': isMouseDown,
      'cursor-grab' : !isMouseDown && shouldSwipe
    }"
    :style="`--carousel-fade-color: ${carouselPreviewFadeColor}; background: inherit;`"
    @touchstart="onTouchStart"
    @touchend="onTouchEnd"
    @touchmove="onTouchMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    @mousemove="onMouseMove"
  >
    <div
      ref="strip"
      class="overflow-hidden flex items-stretch"
      :class="[stripClass, { 'transition-transform duration-500': !isMouseDown }]"
      :style="stripStyle"
    >
      <MajorityUiMajorityCarouselScene
        v-for="(nodes, index) in pageNodes"
        :key="index"
        :nodes="nodes"
        class="w-screen grid justify-center select-none"
        :class="sceneClass"
        :internal-scene-class="internalSceneClass"
        style="grid-template-columns: var(--grid-columns); column-gap: var(--grid-gap);"
      />
    </div>
    <div v-if="showPagination" class="w-full flex justify-center items-center mt-2" :class="paginationClass">
      <button
        v-for="p in numberOfPages"
        :key="p"
        aria-label="pagination"
        class="p-1 focus:outline-none"
        @click="page = p - 1"
      >
        <span
          class="block h-2 w-2 rounded-full transition-colors duration-300 border-1 box-content"
          :class="{ 'opacity-50' : (p - 1 !== page),
                    [paginationDotClass]: true }"
        />
      </button>
    </div>
  </div>
</template>

<script>
import { isNull, isNullOrUndefined } from '@/utils/comparators'
export default {
  provide () {
    return {
      carousel: this
    }
  },
  props: {
    stripClass: {
      type: String,
      default: () => ''
    },
    sceneClass: {
      type: String,
      default: () => ''
    },
    internalSceneClass: {
      type: String,
      default: ''
    },
    paginationClass: {
      type: String,
      default: () => ''
    },
    perPage: {
      type: [Number, String],
      default: () => 1,
      validator: (val) => {
        return !Number.isNaN(parseInt(val))
      }
    },
    perPageMd: {
      type: [Number, String],
      default: () => 0,
      validator: (val) => {
        return !Number.isNaN(parseInt(val))
      }
    },
    perPageLg: {
      type: [Number, String],
      default: () => 0,
      validator: (val) => {
        return !Number.isNaN(parseInt(val))
      }
    },
    slideGap: {
      type: [Number, String],
      default: () => 0
    },
    noPagination: {
      type: Boolean,
      default: () => false
    },
    showFade: {
      type: Boolean,
      default: () => false
    },
    showPreview: {
      type: Boolean,
      default: () => false
    },
    previewPercentage: {
      type: Number,
      default: 10
    },
    previewPercentageMd: {
      type: Number,
      default: 10
    },
    previewPercentageLg: {
      type: Number,
      default: 10
    },
    lightPagination: {
      type: Boolean,
      default: () => false
    }
  },
  data () {
    return {
      page: 0,
      touchStartedAt: null,
      activeSwipeDistance: 0,
      clickStartedAt: null,
      isMouseDown: false,
      allNodes: null,
      pageWidth: 0,
      transparent: true,
      carouselPreviewFadeColor: 'rgba(0,0,0,0)'
    }
  },
  computed: {
    swipeToPageThreshold () {
      return this.pageWidth > 500 ? this.pageWidth / 5 : 100
    },
    numberOfPages () {
      return this.pageNodes.length
    },
    showPagination () {
      return !this.noPagination && this.numberOfPages > 1
    },
    slidesPerPage () {
      if (this.pageWidth >= 992 && this.perPageLg > 0) {
        return this.perPageLg
      } else if (this.pageWidth >= 768 && this.perPageMd > 0) {
        return this.perPageMd
      }
      return this.perPage
    },
    hasPreviousPage () {
      return this.page !== 0 && this.numberOfPages > 1
    },
    hasNextPage () {
      return this.page + 1 < this.numberOfPages
    },
    stripStyle () {
      let previewPercentage = this.previewPercentage

      if (this.pageWidth >= 992) {
        previewPercentage = this.previewPercentageLg
      } else if (this.pageWidth >= 768) {
        previewPercentage = this.previewPercentageMd
      }

      const gridGap = typeof this.slideGap === 'number' ? `${this.slideGap}px` : this.slideGap
      const onePagePercentage = 100 / this.numberOfPages
      const pagePercentage = onePagePercentage * this.page
      const swipePosition = pagePercentage - (this.showPreview && this.page !== 0 ? (onePagePercentage * (previewPercentage)) / 100 : 0)
      return {
        width: `${(this.numberOfPages * 100) * (this.showPreview ? 100 / ((previewPercentage * 2) + 100) : 1)}%`,
        transform: this.numberOfPages !== 0 ? `translateX(${-1 * swipePosition}%) translateX(${-1 * this.activeSwipeDistance}px)` : null,
        '--grid-columns': `repeat(${this.slidesPerPage}, 1fr)`,
        '--grid-gap': gridGap,
        'will-change': 'transform'
      }
    },
    pageNodes () {
      const group = this.slidesPerPage
      if (isNull(this.allNodes)) {
        return []
      }

      return this.allNodes.reduce((result, val, index) => {
        const groupIndex = parseInt(index / group)

        if (!Array.isArray(result[groupIndex])) {
          result[groupIndex] = []
        }

        result[groupIndex].push(val)

        return result
      }, [])
    },
    paginationDotClass () {
      return (this.lightPagination) ? 'bg-bright-yellow border-bright-yellow' : 'bg-infra-violette border-infra-violette'
    },
    shouldSwipe () {
      return this.numberOfPages > 1
    }
  },
  watch: {
    page: {
      handler (curr) {
        this.$emit('slideChange', curr)
      },
      immediate: true
    }
  },
  mounted () {
    this.carouselPreviewFadeColor = getComputedStyle(this.$refs.wrapper).backgroundColor
    window.addEventListener('resize', this.onResize)
    this.pageWidth = this.getElementWidth(document.body)
    this.allNodes = this.$slots.default()[0].children.filter((s) => {
      return !isNullOrUndefined(s)
    })
    this.$nextTick(() => {
      this.transparent = false
    })
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onResize () {
      this.pageWidth = this.getElementWidth(document.body)
    },
    onMouseDown (e) {
      if (!this.shouldSwipe) {
        return
      }

      //  Avoid trigger carousel if it's a link
      if (e.target.tagName.toLowerCase() === 'a') {
        return
      }

      this.clickStartedAt = e.clientX
      this.isMouseDown = true
    },
    onMouseUp (e) {
      if (!this.shouldSwipe) {
        return
      }
      // If mouse leaves the browser window this event triggers regardless
      // of previous state. This clause prevents this.
      if (!this.isMouseDown) {
        return
      }

      this.isMouseDown = false

      const clickEndedAt = e.clientX
      if (this.clickStartedAt - clickEndedAt > this.swipeToPageThreshold) {
        this.nextPage()
      } else if (this.clickStartedAt - clickEndedAt < 0 - this.swipeToPageThreshold) {
        this.prevPage()
      }
      this.activeSwipeDistance = 0
    },
    onMouseMove (e) {
      if (!this.shouldSwipe) {
        return
      }
      if (this.isMouseDown) {
        this.activeSwipeDistance = this.clickStartedAt - e.clientX
      }
    },
    onTouchStart (e) {
      if (!this.shouldSwipe) {
        return
      }
      this.touchStartedAt = e.targetTouches[0].pageX
    },
    onTouchEnd (e) {
      if (!this.shouldSwipe) {
        return
      }
      window.document.body.style.overflow = ''
      const touchEndedAt = e.changedTouches[0].pageX
      if (this.touchStartedAt - touchEndedAt > this.swipeToPageThreshold) {
        this.nextPage()
      } else if (this.touchStartedAt - touchEndedAt < 0 - this.swipeToPageThreshold) {
        this.prevPage()
      }

      this.activeSwipeDistance = 0
    },
    onTouchMove (e) {
      if (!this.shouldSwipe) {
        return
      }

      this.activeSwipeDistance = this.touchStartedAt - e.targetTouches[0].pageX

      if (Math.abs(this.activeSwipeDistance) >= this.swipeToPageThreshold) {
        window.document.body.style.overflow = 'hidden'
      }
    },
    nextPage () {
      if (this.page < this.numberOfPages - 1) {
        this.page++
      }
    },
    prevPage () {
      if (this.page > 0) {
        this.page--
      }
    },
    getElementWidth (element) {
      try {
        return getComputedStyle(element).getPropertyValue('width').match(new RegExp(/[0-9]+/, 'g'))[0]
      } catch (error) {
        return null
      }
    },
    max (value, maxValue) {
      return (value > maxValue) ? maxValue : value
    }
  }
}
</script>

<style lang="postcss" scoped>
@screen lg {
  .carousel-fade-both::after {
    @apply block absolute top-0 left-0 h-full w-full pointer-events-none;
    background-image:
      linear-gradient(to right, rgba(255, 255, 255, 0) 90%, var(--carousel-fade-color) 100%),
      linear-gradient(to left, rgba(255, 255, 255, 0) 90%, var(--carousel-fade-color) 100%);
    content: '';
  }
  .carousel-fade-next::after {
    @apply block absolute top-0 left-0 h-full w-full pointer-events-none;
    background-image: linear-gradient(to right, rgba(255, 255, 255, 0) 90%, var(--carousel-fade-color) 100%);
    content: '';
  }
  .carousel-fade-previous::after {
    @apply block absolute top-0 left-0 h-full w-full pointer-events-none;
    background-image: linear-gradient(to left, rgba(255, 255, 255, 0) 90%, var(--carousel-fade-color) 100%);
    content: '';
  }
}
</style>
